import type { Asset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { ABSENT, UNREGISTERED } from '../constants';

import { IconRegistry } from 'js/src/agent/registries/IconRegistry';
import { getColorName } from 'js/src/agent/utils';

/**
 * Render a color as a string with both numeric value and color name.
 * Example: "16777215(white)"
 */
export const renderColorAsString = (color: number): string => {
    return `${color}(${getColorName(color)})`;
};

/**
 * Reverse-map an icon asset to its IconRegistry id — the same id `set_icon` accepts as `media_id`.
 * Mirrors getMediaIdFromAsset for media assets, so the serialized icon carries the handle the agent
 * needs to see which icon is set (and to diff icon swaps). Returns ABSENT when the asset isn't an
 * icon or has no source uuid, and UNREGISTERED when its uuid isn't in the registry yet (e.g. a
 * template default the agent hasn't searched up).
 */
export const getIconIdFromAsset = (asset: Asset): string => {
    if (!VideoData.isIconAsset(asset)) {
        return ABSENT;
    }
    const uuid = asset.sourceData?.uuid;
    if (!uuid) {
        return ABSENT;
    }
    return IconRegistry.getInstance().getItemIdByUuid(uuid) || UNREGISTERED;
};

/**
 * Format icon color maps into a readable string.
 * Returns a comma-separated list of colors with "and" for the last color.
 * Example: "16777215(white), 0(black) and 16711680(red)"
 */
export const formatIconColors = (asset: Asset): string => {
    if (!VideoData.isIconAsset(asset)) {
        return '';
    }

    let colors = '';
    asset.colorMaps.forEach((colorMap, index, array) => {
        if (colors) {
            if (index === array.length - 1) {
                colors += ' and ';
            } else {
                colors += ', ';
            }
        }
        colors += Number.isFinite(colorMap.targetColor)
            ? renderColorAsString(colorMap.targetColor)
            : ABSENT;
    });

    return colors;
};
